import { useId, useLayoutEffect, useRef, useState } from "react";
import {
  DisplaySettings,
  FavSettings,
  NgSettings,
  OtherSettings,
} from "@/features/settings/components/sections";
import {
  isSettingsEvent,
  type SettingsEvent,
} from "@/features/settings/mediators/settingsEvents";
import { createSettingsTabsMediator } from "@/features/settings/mediators/settingsTabsMediator";
import {
  SETTINGS_TABS,
  type SettingsTabDefinition,
  type SettingsTabId,
} from "@/features/settings/utils/settingsTabs";
import { useCurrentUrlSlug } from "@/shared/hooks/useCurrentUrlSlug";
import { cn } from "@/shared/lib";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import {
  useComponentNode,
  useHierarchyLifetime,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";
import { Modal } from "@/shared/ui/overlay";

interface DesktopSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TABS: readonly SettingsTabDefinition[] = SETTINGS_TABS;

interface DesktopSettingsModalViewProps extends DesktopSettingsModalProps {
  readonly activeTab: SettingsTabId;
  readonly currentUrlSlug: string | null;
  readonly onEvent: (
    event:
      | {
          readonly type: "settings/tab-selected";
          readonly tabId: SettingsTabId;
        }
      | { readonly type: "settings/tab-key-pressed"; readonly key: string },
  ) => SettingsTabId | null;
}

/**
 * PC版の設定モーダル。SP版の`SettingsPage`が使う既存の設定セクション
 * (DisplaySettings/NgSettings/FavSettings/OtherSettings)をタブ切替で
 * そのまま内包する。設定の状態管理(各セクション内のZustandストア)は
 * SP版と共用し、外枠のみモーダル用に作り直す(#34)。
 *
 * タブ切替はWAI-ARIA APGのTab pattern(role="tablist"/"tab"/"tabpanel"、
 * roving tabindex)に沿って実装する。
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/tabs/
 */
export const DesktopSettingsModal: React.FunctionComponent<
  DesktopSettingsModalProps
> = ({ isOpen, onClose }: DesktopSettingsModalProps) => {
  const [mediator] = useState(() => createSettingsTabsMediator(isOpen));
  useHierarchyLifetime(mediator);
  const node = useComponentNode((event) => {
    if (!isSettingsEvent(event)) return "unhandled";
    if (
      event.type !== "settings/tab-selected" &&
      event.type !== "settings/tab-key-pressed" &&
      event.type !== "settings/modal-visibility-changed"
    ) {
      return "unhandled";
    }
    return mediator.dispatch(event).status === "handled"
      ? "handled"
      : "unhandled";
  });
  const { activeTab } = useMediatorSnapshot(mediator);
  const currentUrlSlug = useCurrentUrlSlug();

  useLayoutEffect(() => {
    const event: SettingsEvent = {
      type: "settings/modal-visibility-changed",
      isOpen,
    };
    node.dispatch(event);
  }, [isOpen, node]);

  return (
    <ComponentScope node={node}>
      <DesktopSettingsModalView
        isOpen={isOpen}
        onClose={onClose}
        activeTab={activeTab}
        currentUrlSlug={currentUrlSlug}
        onEvent={(event): SettingsTabId | null => {
          const result = node.dispatch(event);
          return result.status === "handled"
            ? mediator.getSnapshot().activeTab
            : null;
        }}
      />
    </ComponentScope>
  );
};

export const DesktopSettingsModalView: React.FunctionComponent<
  DesktopSettingsModalViewProps
> = ({
  isOpen,
  onClose,
  activeTab,
  currentUrlSlug,
  onEvent,
}: DesktopSettingsModalViewProps) => {
  const baseId = useId();
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const handleTabKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
  ): void => {
    const nextTabId = onEvent({
      type: "settings/tab-key-pressed",
      key: event.key,
    });
    if (nextTabId === null) return;
    const nextIndex = TABS.findIndex((tab) => tab.id === nextTabId);
    event.preventDefault();
    tabRefs.current[nextIndex]?.focus();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      position="top"
      title="設定"
      width="settings"
    >
      <div
        role="tablist"
        aria-label="設定カテゴリ"
        className="sticky top-0 z-10 flex min-w-0 overflow-x-auto border-b border-border bg-card"
      >
        {TABS.map((tab, index) => {
          const selected = activeTab === tab.id;
          return (
            <button
              type="button"
              key={tab.id}
              ref={(element: HTMLButtonElement | null): void => {
                tabRefs.current[index] = element;
              }}
              id={`${baseId}-tab-${tab.id}`}
              role="tab"
              aria-selected={selected}
              aria-controls={`${baseId}-panel-${tab.id}`}
              tabIndex={selected ? 0 : -1}
              className={cn(
                "flex-1 shrink-0 whitespace-nowrap px-4 py-3 text-sm font-medium transition-colors",
                selected
                  ? "text-primary border-b-2 border-primary"
                  : "text-muted-foreground hover:text-foreground",
              )}
              onClick={(): void => {
                onEvent({ type: "settings/tab-selected", tabId: tab.id });
              }}
              onKeyDown={handleTabKeyDown}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      <div
        id={`${baseId}-panel-${activeTab}`}
        role="tabpanel"
        aria-labelledby={`${baseId}-tab-${activeTab}`}
        className="p-4 space-y-6"
      >
        {(activeTab === "display" ||
          activeTab === "catalog" ||
          activeTab === "thread") && (
          <DisplaySettings
            category={activeTab}
            currentUrlSlug={currentUrlSlug}
          />
        )}
        {activeTab === "ng" && <NgSettings />}
        {activeTab === "fav" && <FavSettings />}
        {activeTab === "other" && <OtherSettings />}
      </div>
    </Modal>
  );
};
